'use client'

/* ---------------------------------
Events
--------------------------------- */

import React, { ReactElement, useEffect, useState } from 'react'
import {
  InfoBanner,
  OpeningTitle,
  Section,
  Title,
} from '@dynamo-caffeina-dev/airc-ui-kit'
import { SectionBackground } from '@/types'
import DOMPurify from 'isomorphic-dompurify'
import { EventsModel } from '@/models/Events'
import Spinner from '@/components/atoms/Spinner'
import { NormalizedEventType } from '@/types/NormalizedEventType'
import EventCard from '@/components/molecules/EventCard'
import TemporalNavigation from '@/components/molecules/TemporalNavigation'
import { PaginationType } from '@/types/PaginationType'

type OwnProps = {
  props: ReturnType<typeof EventsModel>['props']
  modelledContent: ReturnType<typeof EventsModel>['modelledContent']
}

export default function Events({
  props,
  modelledContent: extractedContent,
}: OwnProps): ReactElement {
  const { openingTitle, title: _title, config } = extractedContent ?? {}
  const { content: titleContent, ...title } = _title ?? {}
  const { bg } = props ?? {}

  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')
  const [events, setEvents] = useState<NormalizedEventType[]>([])

  const [selectedRegion, setSelectedRegion] = useState<string>('')
  const [timeDirection, setTimeDirection] = useState<'prossimi' | 'passati'>(
    'prossimi'
  )
  const [monthsCount, setMonthsCount] = useState<number>(4)

  const [currentPage, setCurrentPage] = useState<number>(1)
  const [paginationMeta, setPaginationMeta] = useState<PaginationType | null>(
    null
  )

  useEffect(() => {
    if (!config) {
      setLoading(false)
      return
    }

    setCurrentPage(1)
    fetchEvents(selectedRegion, 1)
  }, [selectedRegion, timeDirection, monthsCount, config])

  async function fetchEvents(region: string, page: number = currentPage) {
    if (!config?.url) return

    setLoading(true)
    setError('')

    try {
      const tagsParam = config.tags ? config.tags.join(',') : ''
      const pastEventsParam = config.pastevents

      const requestUrl =
        `/api/events?apiUrl=${encodeURIComponent(config.url)}` +
        // Add all CMS-driven tags, if any
        (tagsParam ? `&tags=${tagsParam}` : '') +
        // Add user-selected region, if any
        (region ? `&regions=${region}` : '') +
        // Handles `pastevents` param if present - when 1,
        // user can choose direction of time scanning
        (typeof pastEventsParam === 'number'
          ? `&pastevents=${pastEventsParam === 1 && timeDirection === 'passati' ? 1 : 0}`
          : '') +
        // Add months count (per_page)
        `&per_page=${monthsCount}` +
        // Add pagination parameters
        `&page=${page}`

      const response = await fetch(requestUrl)

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`)
      }

      const events = await response.json()
      setEvents(events.data || [])
      setPaginationMeta(events.meta?.pagination || null)
    } catch (err) {
      console.error('Error fetching events:', err)
      setError((err as Error).message || 'Impossibile caricare gli eventi')
      setEvents([])
    } finally {
      setLoading(false)
    }
  }

  const handleRegionChange = (region: string) => {
    setSelectedRegion(region)
  }

  const handleTimeDirectionChange = (direction: 'prossimi' | 'passati') => {
    setTimeDirection(direction)
    setCurrentPage(1)
  }

  const handleMonthsCountChange = (count: number) => {
    setMonthsCount(count)
    setCurrentPage(1)
  }

  const handlePeriodChange = (direction: 'previous' | 'next') => {
    const newPage = direction === 'previous' ? currentPage - 1 : currentPage + 1
    // TODO if newPage?
    setCurrentPage(newPage)
    fetchEvents(selectedRegion, newPage)
  }

  return (
    <Section
      // `bg` is not provided by the CMS in this component,
      // but it should be; so I'm leaving this here for now.
      bg={bg as SectionBackground | undefined}
      className={`section-events`}
    >
      <div className='section-events__wrapper'>
        {/* TITLE */}
        <div className={`section-events__title-wrapper`}>
          {openingTitle.children ? (
            <OpeningTitle
              className={`section-testimonial-slider__opening-title`}
              {...openingTitle}
            />
          ) : null}

          {titleContent ? (
            <Title
              className={`section-events__title`}
              {...title}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(titleContent),
              }}
            />
          ) : null}
        </div>

        {/* CONTENT */}
        <div className='section-events__content'>
          {/* Loading state */}
          {loading ? (
            <div className='section-events__loading'>
              <Spinner
                size='medium'
                color='var(--airc-ui__colors-accent-500)'
              />
            </div>
          ) : null}

          {/* Error state */}
          {error ? (
            <div className='section-events__error'>
              <InfoBanner
                icon={'Error'}
                variant={'error'}
              >
                {error}
              </InfoBanner>
            </div>
          ) : null}

          {/* Temporal navigation */}
          {paginationMeta ? (
            <TemporalNavigation
              meta={paginationMeta}
              timeDirection={timeDirection}
              monthsCount={monthsCount}
              selectedRegion={selectedRegion}
              regions={config?.regions}
              // The CMS user allowed past events to be visible
              showTimeDirection={config?.pastevents === 1}
              onTimeDirectionChange={handleTimeDirectionChange}
              onMonthsCountChange={handleMonthsCountChange}
              onRegionChange={handleRegionChange}
              onPeriodChange={handlePeriodChange}
              loading={loading}
            />
          ) : null}

          {/* Events list */}
          {!loading && !error ? (
            <div className='section-events__list'>
              {events.length ? (
                events.map((event, i) => (
                  <EventCard
                    key={`evt__${i}`}
                    event={event}
                    index={i}
                  />
                ))
              ) : (
                <div className='section-events__blank-slate'>
                  <InfoBanner
                    icon={'Info'}
                    variant={'info'}
                  >
                    Nessun evento trovato
                    {selectedRegion ? ` nella regione selezionata` : ''}.
                  </InfoBanner>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </Section>
  )
}
